import { useEffect } from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import {
  classReportDetails,
  readingCohorts,
} from "../../utils/CommonUtils/constants";
import { getCurrentDate } from "../../utils/helper";
import useParamValues from "../../utils/useParamValues";
import html2pdf from "html2pdf.js";
import girlHoldingPhoneWordCloud from "../../assets/girl_holding_phone_word_cloud.svg";
import goldMedalImg from "../../assets/gold-medal-img.svg";
import metricOneIcon from "../../assets/metric_1.svg";
import metricTwoIcon from "../../assets/metric_2.svg";
import metricThreeIcon from "../../assets/metric_3.svg";
import CohortLevelBarSection from "../../components/CohortLevelBar/CohortLevelBarSection";
import CohortisedStudentList from "./cohortisedStudentList/CohortisedStudentList";
import useTranslationSetup from "../../utils/useTranslationSetup";
import "./styles.css";
import useWebViewBridge from "../../utils/useWebViewBridge";
import SubjectTag from "../../components/subjectTag/SubjectTag";

const COHORT_METRIC_ICONS = [metricOneIcon, metricTwoIcon, metricThreeIcon];

const ClassReportCardPdf = ({
  showPdfContent,
  setShowPdfContent,
  students,
  paginatedPages,
  categories,
  onPdfReady,
  onPdfError,
}) => {
  const { t, tSubject } = useTranslationSetup();
  const classReportInfo = classReportDetails(
    t,
    students.length,
    useParamValues()
  );
  const { sendMessageToReactNative } = useWebViewBridge();

  const downloadPdfContent = () => {
    const element = document.getElementById("pdf-content");
    if (!element) {
      if (typeof onPdfError === "function") {
        onPdfError(new Error("PDF content element not found"));
      }
      setShowPdfContent(false);
      return;
    }
    html2pdf()
      .set({
        margin: 0.1,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { dpi: 192, letterRendering: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { after: ".pdf-page" },
        filename: "class-report-card.pdf",
      })
      .from(element)
      .outputPdf("datauristring")
      .then((dataUri) => {
        const base64 = dataUri.split(",")[1];
        sendMessageToReactNative(
          JSON.stringify({ type: "download_pdf", data: base64 })
        );
        if (typeof onPdfReady === "function") {
          onPdfReady(dataUri);
        }
      })
      .catch((error) => {
        if (typeof onPdfError === "function") {
          onPdfError(error);
        }
      })
      .finally(() => {
        setShowPdfContent(false);
      });
  };

  useEffect(() => {
    if (showPdfContent) {
      setTimeout(() => {
        downloadPdfContent();
      }, 100);
    }
  }, [showPdfContent]);

  return (
    <Box id="pdf-content">
      {/* *********************************** First page of pdf ********************************** */}
      <Box className="pdf-page">
        <Box className="report-card-pdf-container">
          <Box className="pdf-first-page-top-row">
            <Typography className="current-date-text">
              {`${t("date")}: ${getCurrentDate()}`}
            </Typography>
            <SubjectTag />
          </Box>
          <Box className="girl-holding-phone-word-cloud-img-headers-container">
            <img
              src={girlHoldingPhoneWordCloud}
              alt="girl-holding-phone-word-cloud"
              className="girl-holding-phone-word-cloud-img"
            />

            <Typography className="report-card-pdf-header">
              {t("shala_darpan_app")}
            </Typography>
            <Typography className="report-card-pdf-sub-header">
              {`${t("orf_performance_assessment", {
                assessmentNumber: 1,
              })}`}
            </Typography>
          </Box>

          <Box className="class-report-details-container">
            <Grid className="class-report-details-grid" container spacing={2}>
              {classReportInfo.map((item, index) => (
                <Grid
                  className="class-report-details-grid-item"
                  item
                  xs={6}
                  key={index}
                >
                  <Typography className="class-report-details-grid-item-label">
                    {item.label}
                  </Typography>
                  <Typography className="class-report-details-grid-item-value">
                    {item.value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box className="class-report-participation-congrats-container">
            <Typography className="class-report-participation-congrats-header">
              {t("many_congratulations")}
            </Typography>
            <Box className="class-report-participation-congrats-img-description-container">
              <Box className="gold-medal-img-container">
                <img src={goldMedalImg} alt="gold-medal-img" />
              </Box>
              <Typography className="class-report-participation-congrats-description">
                {t("orf_assessment_participation_message")}
              </Typography>
            </Box>
          </Box>

          <Box className="result-metric-explaination-container">
            <Typography className="result-metric-explaination-header">
              {t("understand_performance_indicators")}
            </Typography>
            <Box className="pdf-cohort-metric-icons">
              {COHORT_METRIC_ICONS.map((icon, index) => (
                <img
                  key={index}
                  src={icon}
                  alt=""
                  aria-hidden="true"
                  className="pdf-cohort-metric-icon"
                />
              ))}
            </Box>
            <Typography className="pdf-cohort-metric-description">
              {t("cohort_metrics_description")}
            </Typography>
          </Box>

          <Box className="explore-reading-cohorts-container">
            <Typography className="explore-reading-cohorts-header">
              {t("understand_reading_groups")}
            </Typography>
            <Box className="reading-cohort-details-container">
              {readingCohorts(tSubject).map((item, index) => (
                <Box className="reading-cohort-details" key={index}>
                  <Box className="reading-cohort-icon-name-container">
                    <img
                      className="reading-cohort-icon"
                      src={item.icon}
                      alt="reading-cohort-icon"
                    />
                    <Typography className="reading-cohort-name">
                      {item.label}
                    </Typography>
                  </Box>
                  <Divider
                    className={`reading-cohort-divider border-color-${index}`}
                  />
                  <Typography className="reading-cohort-description">
                    {item.description}
                  </Typography>
                </Box>
              ))}
            </Box>
            <CohortLevelBarSection
              showCohortMetric={false}
              categories={categories}
            />
          </Box>
        </Box>
      </Box>

      {/* *********************************** Page showing cohortised list of students ********************************** */}

      {paginatedPages.map((cohorts, pageIndex) => (
        <Box className="pdf-page" key={pageIndex}>
          <CohortisedStudentList
            cohorts={cohorts}
            pageIndex={pageIndex}
            classLength={students.length}
          />
        </Box>
      ))}
    </Box>
  );
};

export default ClassReportCardPdf;
